<template>
	<div
		:class="[
			blockClass,
			{
				[`${blockClass}--edit`]: editable,
				'is-active': editable && active,
				[`${blockClass}--h5`]: h5,
				[`${blockClass}--full`]: blockStyle.fullWidth,
				[`${blockClass}--padded`]: !blockStyle.fullWidth,
			},
		]"
		:style="rootStyle"
		@click="onWrapClick"
	>
		<div :class="`${blockClass}__list`">
			<div
				v-for="(item, index) in items"
				:key="item.id"
				:class="`${blockClass}__item`"
			>
				<div
					:class="[`${blockClass}__thumb`, { 'is-previewable': isPreviewable }]"
					:style="thumbStyle"
					@click="onThumbClick(index, $event)"
				>
					<img v-if="getItemImageUrl(item)" :src="getItemImageUrl(item)" alt="" />
					<i v-else class="mbm-iconfont mbm-tupian"></i>
				</div>
			</div>
		</div>

		<ImageGalleryLightbox
			v-model:visible="lightboxVisible"
			:urls="lightboxUrls"
			:initial-index="lightboxIndex"
		/>
	</div>
</template>

<script>
	import ImageGalleryLightbox from '../../../components/ImageGalleryLightbox.vue';
	import {
		getImageMosaicBlockStyle,
		getImageMosaicGalleryUrls,
		getImageMosaicImageUrl,
		normalizeImageMosaicData,
	} from '../../../utils/imageMosaic.js';
	import {
		getImageQuadBlockStyle,
		getImageQuadGalleryUrls,
		getImageQuadImageUrl,
		normalizeImageQuadData,
	} from '../../../utils/imageQuad.js';

	const VARIANT_CONFIG = {
		mosaic: {
			blockClass: 'g-block-image-mosaic',
			colCount: 3,
			normalizeData: normalizeImageMosaicData,
			getBlockStyle: getImageMosaicBlockStyle,
			getImageUrl: getImageMosaicImageUrl,
			getGalleryUrls: getImageMosaicGalleryUrls,
		},
		quad: {
			blockClass: 'g-block-image-quad',
			colCount: 4,
			normalizeData: normalizeImageQuadData,
			getBlockStyle: getImageQuadBlockStyle,
			getImageUrl: getImageQuadImageUrl,
			getGalleryUrls: getImageQuadGalleryUrls,
		},
	};

	export default {
		name: 'GPureImageGridShell',
		components: { ImageGalleryLightbox },
		props: {
			variant: {
				type: String,
				required: true,
				validator: value => ['mosaic', 'quad'].includes(value),
			},
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select'],
		data() {
			return {
				lightboxVisible: false,
				lightboxUrls: [],
				lightboxIndex: 0,
			};
		},
		computed: {
			config() {
				return VARIANT_CONFIG[this.variant];
			},
			blockClass() {
				return this.config.blockClass;
			},
			normalizedData() {
				return this.config.normalizeData(this.data);
			},
			items() {
				return this.normalizedData.items;
			},
			blockStyle() {
				return this.config.getBlockStyle(this.data, { h5: this.h5 });
			},
			thumbStyle() {
				return {
					aspectRatio: this.blockStyle.aspectRatio,
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			rootStyle() {
				return {
					'--pure-image-grid-cols': this.config.colCount,
					'--pure-image-grid-gap': this.blockStyle.colGap,
					'--pure-image-pc-inset': this.blockStyle.pcSideInset,
					'--pure-image-h5-inset': this.blockStyle.h5SideInset,
				};
			},
			isPreviewable() {
				return this.h5 && !this.editable;
			},
		},
		methods: {
			getItemImageUrl(item) {
				return this.config.getImageUrl(item?.image);
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			onThumbClick(index, e) {
				if (!this.isPreviewable) return;
				e?.stopPropagation?.();
				const urls = this.config.getGalleryUrls(this.data);
				if (!urls.length) return;
				this.lightboxUrls = urls;
				this.lightboxIndex = Math.min(index, urls.length - 1);
				this.lightboxVisible = true;
			},
		},
	};
</script>

<style scoped lang="less">
	@import './pureImageGridShell.less';
</style>
